
import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  CircularProgress,
  Divider,
  Button,
  MenuItem,
  TextField,
  Stack,
  LinearProgress,
  Avatar,
  Chip,
} from '@mui/material';

import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';

import {
  Business,
  Hotel,
  Bed,
  People,
  TrendingUp,
  Search,
  Phone,
  CalendarMonth,
} from '@mui/icons-material';

import HeaderMenu from '../Header/HeaderMenu';
import axiosApi from '../Utils/axiosApi';
import { API_BASE_URL } from '../../config';

const AdminDashboard = () => {
  const [hostels, setHostels] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState('');

  /* ===================== INITIAL LOAD ===================== */
  useEffect(() => {
    fetchCompanies();
  }, []);

  /* ===================== DASHBOARD API ===================== */
  const fetchDashboard = async (companyId = null) => {
    try {
      setLoading(true);

      const res = await axiosApi.post(
        '/dashBoard/geHostelSummaryDashboard',
        {
          selectedCompanyId: companyId || null,
        }
      );

      const flattened = (
        Array.isArray(res.data) ? res.data : []
      ).flatMap((op) =>
        (op.hostelDashBoardDTOs || []).map((h) => ({
          ...h,
          operatorCompanyName: op.operatorCompanyName,
        }))
      );

      setHostels(flattened);
    } catch (e) {
      console.error('Dashboard loading error:', e);
      setHostels([]);
    } finally {
      setLoading(false);
    }
  };

  /* ===================== COMPANY API ===================== */
  const fetchCompanies = () => {
    const companyRequestPayload = {};

    axiosApi
      .post(
        `${API_BASE_URL}/company/getCompany`,
        companyRequestPayload
      )
      .then((response) => {
        const companyArray = Array.isArray(response.data)
          ? response.data
          : response.data?.companies || [];

        setCompanies(companyArray);
      })
      .catch((err) => console.error('Company loading error:', err));
  };

  /* ===================== SEARCH ===================== */
  const handleSearch = () => {
    setSearched(true);
    fetchDashboard(selectedCompany);
  };

  /* ===================== FILTER ===================== */
  const filteredHostels = hostels;

  /* ===================== KPI ===================== */
  const kpis = useMemo(() => {
    const totalBeds = filteredHostels.reduce(
      (sum, h) => sum + (Number(h.totalCapacity) || 0),
      0
    );

    const occupiedBeds = filteredHostels.reduce(
      (sum, h) => sum + (Number(h.occupied) || 0),
      0
    );

    const occupancyPercent =
      totalBeds > 0
        ? Math.round((occupiedBeds / totalBeds) * 100)
        : 0;

    return {
      totalBeds,
      occupiedBeds,
      occupancyPercent,
    };
  }, [filteredHostels]);

  /* ===================== KPI CARD ===================== */
  const KpiCard = ({
    title,
    value,
    icon,
    subtitle,
    color,
    bg,
  }) => (
    <Card
      elevation={0}
      sx={{
        height: '100%',
        border: '1px solid #E8EDF3',
        borderRadius: 3,
        transition: 'all 0.2s ease',
        '&:hover': {
          boxShadow: '0 8px 24px rgba(15, 23, 42, 0.08)',
          transform: 'translateY(-2px)',
        },
      }}
    >
      <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={2}
        >
          <Box>
            <Typography
              variant="body2"
              color="text.secondary"
              fontWeight={600}
              mb={1}
            >
              {title}
            </Typography>

            <Typography
              variant="h4"
              fontWeight={800}
              color="#172B4D"
            >
              {value}
            </Typography>

            {subtitle && (
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                mt={0.7}
              >
                {subtitle}
              </Typography>
            )}
          </Box>

          <Avatar
            sx={{
              width: 52,
              height: 52,
              bgcolor: bg,
              color,
              borderRadius: 2.5,
            }}
          >
            {icon}
          </Avatar>
        </Stack>
      </CardContent>
    </Card>
  );

  /* ===================== SECTION TITLE ===================== */
  const SectionTitle = ({ title, subtitle }) => (
    <Box mb={2}>
      <Typography
        variant="subtitle1"
        fontWeight={800}
        color="#172B4D"
      >
        {title}
      </Typography>

      {subtitle && (
        <Typography variant="caption" color="text.secondary">
          {subtitle}
        </Typography>
      )}
    </Box>
  );

  return (
    <>
      <HeaderMenu />

      <Box
        sx={{
          minHeight: '100vh',
          bgcolor: '#F5F7FB',
          px: { xs: 1.5, sm: 2.5, md: 3.5 },
          py: { xs: 2, md: 3 },
        }}
      >
        {/* ===================== PAGE HEADER ===================== */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', sm: 'center' },
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 1.5,
            mb: 3,
          }}
        >
          <Box>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Avatar
                sx={{
                  bgcolor: '#E8EEFF',
                  color: '#3157C8',
                  width: 42,
                  height: 42,
                  borderRadius: 2,
                }}
              >
                <Business />
              </Avatar>

              <Box>
                <Typography
                  variant="h5"
                  fontWeight={800}
                  color="#172B4D"
                >
                  Admin Dashboard
                </Typography>

                <Typography variant="body2" color="text.secondary">
                  Monitor hostel properties, occupancy and performance
                </Typography>
              </Box>
            </Stack>
          </Box>

          {searched && (
            <Chip
              icon={<Hotel />}
              label={`${filteredHostels.length} Properties`}
              sx={{
                bgcolor: '#EAF0FF',
                color: '#3157C8',
                fontWeight: 700,
                borderRadius: 2,
              }}
            />
          )}
        </Box>

        {/* ===================== FILTER SECTION ===================== */}
        <Card
          elevation={0}
          sx={{
            border: '1px solid #E8EDF3',
            borderRadius: 3,
            mb: 3,
          }}
        >
          <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              mb={2}
            >
              <Search sx={{ color: '#3157C8' }} />

              <Typography fontWeight={800} color="#172B4D">
                Property Filters
              </Typography>
            </Stack>

            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={5} lg={4}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Select Company"
                  value={selectedCompany}
                  onChange={(e) =>
                    setSelectedCompany(e.target.value)
                  }
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: '#FFFFFF',
                    },
                  }}
                >
                  <MenuItem value="">
                    All Companies
                  </MenuItem>

                  {companies.map((c) => (
                    <MenuItem
                      key={c.companyId || c.id}
                      value={c.companyId || c.id}
                    >
                      {c.companyName}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} md={3}>
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<Search />}
                  onClick={handleSearch}
                  disabled={loading}
                  sx={{
                    height: 40,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 700,
                    bgcolor: '#3157C8',
                    boxShadow: 'none',
                    '&:hover': {
                      bgcolor: '#2446A8',
                      boxShadow: 'none',
                    },
                  }}
                >
                  {loading ? 'Loading...' : 'Search'}
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* ===================== INITIAL STATE ===================== */}
        {!searched && (
          <Card
            elevation={0}
            sx={{
              border: '1px dashed #CBD5E1',
              borderRadius: 3,
              textAlign: 'center',
              py: 7,
              px: 2,
              bgcolor: '#FFFFFF',
            }}
          >
            <Avatar
              sx={{
                width: 64,
                height: 64,
                mx: 'auto',
                mb: 2,
                bgcolor: '#EEF2FF',
                color: '#3157C8',
              }}
            >
              <Business sx={{ fontSize: 32 }} />
            </Avatar>

            <Typography fontWeight={800} color="#172B4D" mb={0.5}>
              Welcome to Admin Dashboard
            </Typography>

            <Typography variant="body2" color="text.secondary">
              Select a company and click Search to view property performance.
            </Typography>
          </Card>
        )}

        {/* ===================== DASHBOARD CONTENT ===================== */}
        {searched && (
          <>
            {loading && (
              <Box textAlign="center" py={4}>
                <CircularProgress />

                <Typography
                  variant="body2"
                  color="text.secondary"
                  mt={1}
                >
                  Loading dashboard...
                </Typography>
              </Box>
            )}

            {!loading && (
              <>
                {/* ===================== KPI CARDS ===================== */}
                <Box mb={3}>
                  <SectionTitle
                    title="Overall Performance"
                    subtitle="Combined capacity and occupancy across the selected properties"
                  />

                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={4}>
                      <KpiCard
                        title="Total Beds"
                        value={kpis.totalBeds}
                        subtitle="Total property capacity"
                        icon={<Bed sx={{ fontSize: 27 }} />}
                        color="#3157C8"
                        bg="#EAF0FF"
                      />
                    </Grid>

                    <Grid item xs={12} sm={6} md={4}>
                      <KpiCard
                        title="Occupied Beds"
                        value={kpis.occupiedBeds}
                        subtitle="Currently occupied beds"
                        icon={<People sx={{ fontSize: 27 }} />}
                        color="#15966B"
                        bg="#E4F7EF"
                      />
                    </Grid>

                    <Grid item xs={12} sm={6} md={4}>
                      <KpiCard
                        title="Occupancy"
                        value={`${kpis.occupancyPercent}%`}
                        subtitle="Overall occupancy percentage"
                        icon={<TrendingUp sx={{ fontSize: 27 }} />}
                        color="#D97706"
                        bg="#FFF3DF"
                      />
                    </Grid>
                  </Grid>
                </Box>

                {/* ===================== EMPTY DATA ===================== */}
                {filteredHostels.length === 0 && (
                  <Card
                    elevation={0}
                    sx={{
                      border: '1px solid #E8EDF3',
                      borderRadius: 3,
                      textAlign: 'center',
                      py: 6,
                    }}
                  >
                    <Hotel
                      sx={{
                        fontSize: 42,
                        color: '#94A3B8',
                        mb: 1,
                      }}
                    />

                    <Typography fontWeight={700} color="#172B4D">
                      No hostel data found
                    </Typography>

                    <Typography variant="body2" color="text.secondary">
                      No properties are available for the selected company.
                    </Typography>
                  </Card>
                )}

                {/* ===================== HOSTEL LIST ===================== */}
                <Grid container spacing={2.5}>
                  {filteredHostels.map((h) => {
                    const occupancy =
                      h.totalCapacity > 0
                        ? Math.round(
                            (h.occupied / h.totalCapacity) * 100
                          )
                        : 0;

                    const capacityChartData = [
                      {
                        category: 'Overview',
                        Capacity: h.totalCapacity ?? 0,
                        Occupied: h.occupied ?? 0,
                        Vacancy: h.availableVacancy ?? 0,
                        'Food Only': h.foodOnlyHostelers ?? 0,
                        'Stay Only': h.stayOnlyHostelers ?? 0,
                        'Stay + Food': h.stayWithFoodHostelers ?? 0,
                      },
                    ];

                    return (
                      <Grid item xs={12} key={h.hostelId}>
                        <Card
                          elevation={0}
                          sx={{
                            border: '1px solid #E5EAF1',
                            borderRadius: 3,
                            overflow: 'hidden',
                            bgcolor: '#FFFFFF',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              boxShadow:
                                '0 8px 26px rgba(15, 23, 42, 0.07)',
                            },
                          }}
                        >
                          {/* HOSTEL HEADER */}
                          <Box
                            sx={{
                              p: { xs: 2, md: 2.5 },
                              bgcolor: '#FFFFFF',
                            }}
                          >
                            <Stack
                              direction={{ xs: 'column', sm: 'row' }}
                              justifyContent="space-between"
                              alignItems={{
                                xs: 'flex-start',
                                sm: 'center',
                              }}
                              spacing={2}
                            >
                              <Stack
                                direction="row"
                                alignItems="center"
                                spacing={1.5}
                              >
                                <Avatar
                                  sx={{
                                    width: 46,
                                    height: 46,
                                    bgcolor: '#EAF0FF',
                                    color: '#3157C8',
                                    borderRadius: 2,
                                  }}
                                >
                                  <Hotel />
                                </Avatar>

                                <Box>
                                  <Typography
                                    variant="h6"
                                    fontWeight={800}
                                    color="#172B4D"
                                  >
                                    {h.hostelName}
                                  </Typography>

                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    {h.operatorCompanyName}
                                  </Typography>
                                </Box>
                              </Stack>

                              <Chip
                                icon={<TrendingUp />}
                                label={`Occupancy ${occupancy}%`}
                                size="small"
                                sx={{
                                  bgcolor:
                                    occupancy >= 80
                                      ? '#E4F7EF'
                                      : occupancy >= 50
                                      ? '#FFF3DF'
                                      : '#EEF2FF',
                                  color:
                                    occupancy >= 80
                                      ? '#12805C'
                                      : occupancy >= 50
                                      ? '#B66A08'
                                      : '#3157C8',
                                  fontWeight: 800,
                                  borderRadius: 2,
                                }}
                              />
                            </Stack>

                            <Stack
                              direction="row"
                              alignItems="center"
                              spacing={1}
                              mt={2}
                            >
                              <Phone
                                sx={{
                                  fontSize: 17,
                                  color: '#64748B',
                                }}
                              />

                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {h.mobile || 'N/A'}
                              </Typography>
                            </Stack>

                            <Box mt={2}>
                              <Stack
                                direction="row"
                                justifyContent="space-between"
                                mb={0.8}
                              >
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  fontWeight={600}
                                >
                                  Bed Occupancy
                                </Typography>

                                <Typography
                                  variant="caption"
                                  fontWeight={800}
                                  color="#172B4D"
                                >
                                  {h.occupied ?? 0} / {h.totalCapacity ?? 0}
                                </Typography>
                              </Stack>

                              <LinearProgress
                                variant="determinate"
                                value={Math.min(
                                  100,
                                  Math.max(0, occupancy)
                                )}
                                sx={{
                                  height: 8,
                                  borderRadius: 5,
                                  bgcolor: '#E9EEF5',
                                  '& .MuiLinearProgress-bar': {
                                    borderRadius: 5,
                                    bgcolor:
                                      occupancy >= 80
                                        ? '#15966B'
                                        : occupancy >= 50
                                        ? '#D97706'
                                        : '#3157C8',
                                  },
                                }}
                              />
                            </Box>
                          </Box>

                          <Divider />

                          <CardContent
                            sx={{
                              p: { xs: 2, md: 2.5 },
                              '&:last-child': { pb: 2.5 },
                            }}
                          >
                            <Grid container spacing={2.5}>
                              {/* MONTHLY SUMMARY */}
                              <Grid item xs={12} md={4}>
                                <Card
                                  elevation={0}
                                  sx={{
                                    height: '100%',
                                    border: '1px solid #E8EDF3',
                                    borderRadius: 2.5,
                                    bgcolor: '#FAFBFD',
                                  }}
                                >
                                  <CardContent sx={{ p: 2 }}>
                                    <Stack
                                      direction="row"
                                      alignItems="center"
                                      spacing={1}
                                      mb={2}
                                    >
                                      <Avatar
                                        sx={{
                                          width: 34,
                                          height: 34,
                                          bgcolor: '#FFF0DB',
                                          color: '#D97706',
                                        }}
                                      >
                                        <CalendarMonth fontSize="small" />
                                      </Avatar>

                                      <Typography
                                        fontWeight={800}
                                        color="#172B4D"
                                      >
                                        Monthly Summary
                                      </Typography>
                                    </Stack>

                                    <Divider sx={{ mb: 1.5 }} />

                                    <Stack spacing={1.5}>
                                      <Stack
                                        direction="row"
                                        justifyContent="space-between"
                                      >
                                        <Typography
                                          variant="body2"
                                          color="text.secondary"
                                        >
                                          Callbacks
                                        </Typography>

                                        <Typography fontWeight={800}>
                                          {h.callbacks ?? 0}
                                        </Typography>
                                      </Stack>

                                      <Stack
                                        direction="row"
                                        justifyContent="space-between"
                                      >
                                        <Typography
                                          variant="body2"
                                          color="text.secondary"
                                        >
                                          Visits
                                        </Typography>

                                        <Typography fontWeight={800}>
                                          {h.visits ?? 0}
                                        </Typography>
                                      </Stack>

                                      <Stack
                                        direction="row"
                                        justifyContent="space-between"
                                      >
                                        <Typography
                                          variant="body2"
                                          color="text.secondary"
                                        >
                                          Monthly Bookings
                                        </Typography>

                                        <Typography fontWeight={800}>
                                          {h.monthlyBookingCount ?? 0}
                                        </Typography>
                                      </Stack>
                                    </Stack>
                                  </CardContent>
                                </Card>
                              </Grid>

                              {/* CAPACITY CHART */}
                              <Grid item xs={12} md={8}>
                                <Card
                                  elevation={0}
                                  sx={{
                                    height: '100%',
                                    border: '1px solid #E8EDF3',
                                    borderRadius: 2.5,
                                  }}
                                >
                                  <CardContent sx={{ p: 2 }}>
                                    <Box mb={1}>
                                      <Typography
                                        fontWeight={800}
                                        color="#172B4D"
                                      >
                                        Hostel Capacity & Occupancy
                                      </Typography>

                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                      >
                                        Capacity, occupied beds, vacancy and
                                        accommodation breakdown
                                      </Typography>
                                    </Box>

                                    <Box
                                      sx={{
                                        width: '100%',
                                        height: 280,
                                        minWidth: 0,
                                      }}
                                    >
                                      <ResponsiveContainer
                                        width="100%"
                                        height="100%"
                                      >
                                        <BarChart
                                          data={capacityChartData}
                                          margin={{
                                            top: 15,
                                            right: 10,
                                            left: 0,
                                            bottom: 5,
                                          }}
                                        >
                                          <CartesianGrid
                                            strokeDasharray="3 3"
                                            vertical={false}
                                            stroke="#E8EDF3"
                                          />

                                          <XAxis
                                            dataKey="category"
                                            tick={{
                                              fill: '#64748B',
                                              fontSize: 12,
                                            }}
                                            axisLine={false}
                                            tickLine={false}
                                          />

                                          <YAxis
                                            allowDecimals={false}
                                            tick={{
                                              fill: '#64748B',
                                              fontSize: 11,
                                            }}
                                            axisLine={false}
                                            tickLine={false}
                                          />

                                          <Tooltip
                                            contentStyle={{
                                              borderRadius: 10,
                                              border: '1px solid #E2E8F0',
                                              boxShadow:
                                                '0 4px 14px rgba(0,0,0,0.08)',
                                            }}
                                          />

                                          <Legend
                                            wrapperStyle={{
                                              fontSize: 11,
                                              paddingTop: 10,
                                            }}
                                          />

                                          <Bar
                                            dataKey="Capacity"
                                            fill="#90CAF9"
                                            radius={[4, 4, 0, 0]}
                                          />

                                          <Bar
                                            dataKey="Occupied"
                                            fill="#66BB6A"
                                            radius={[4, 4, 0, 0]}
                                          />

                                          <Bar
                                            dataKey="Vacancy"
                                            fill="#EF5350"
                                            radius={[4, 4, 0, 0]}
                                          />

                                          <Bar
                                            dataKey="Food Only"
                                            fill="#42A5F5"
                                            radius={[4, 4, 0, 0]}
                                          />

                                          <Bar
                                            dataKey="Stay Only"
                                            fill="#CE93D8"
                                            radius={[4, 4, 0, 0]}
                                          />

                                          <Bar
                                            dataKey="Stay + Food"
                                            fill="#81C784"
                                            radius={[4, 4, 0, 0]}
                                          />
                                        </BarChart>
                                      </ResponsiveContainer>
                                    </Box>
                                  </CardContent>
                                </Card>
                              </Grid>
                            </Grid>
                          </CardContent>
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>
              </>
            )}
          </>
        )}
      </Box>
    </>
  );
};

export default AdminDashboard;
